import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { useDebounced } from '@/lib/catalogue';
import { useCustomers } from '@/lib/orders';
import { useSession } from '@/lib/session';
import { formatMoney } from '@/lib/utils';

type Gift = {
  tierId: number;
  value: string;
  giftName: string;
  claimedCount: number;
  claimable: boolean;
};
type RewardGroup = {
  groupId: number;
  groupName: string;
  budget: string;
  spent: string;
  remaining: string;
  gifts: Gift[];
};
type ClaimsData = {
  customer: { id: number; name: string; custNum: string };
  groups: RewardGroup[];
  confirmation: { signerName: string; email: string } | null;
  giftsClosed: boolean;
};
type JournalRow = {
  id: number;
  customerId: number;
  tierId: number;
  custNum: string;
  customerName: string;
  groupName: string;
  giftName: string;
  value: string;
  claimedAt: string | null;
  claimedBy: string | null;
};

/**
 * Comptoir cadeaux : chercher un client, puis remettre ses cadeaux selon son
 * BUDGET (total d'achat). Chaque cadeau a une valeur ; on peut le remettre
 * plusieurs fois (compteur +/−) tant que le budget restant du groupe le permet.
 * Pour l'admin : un JOURNAL global de la distribution, avec correction possible.
 */
export default function Claims({ customerId: controlledId }: { customerId?: number | null } = {}) {
  // Mode « contrôlé » : le client est fourni par le parent (comptoir à recherche
  // unique). Sinon, le composant gère sa propre recherche (admin).
  const controlled = controlledId !== undefined;
  const [q, setQ] = useState('');
  const debounced = useDebounced(q, 250);
  const recherche = debounced.trim() !== '';
  const clients = useCustomers(debounced, recherche);
  const [ownCustomerId, setOwnCustomerId] = useState<number | null>(null);
  const customerId = controlled ? (controlledId ?? null) : ownCustomerId;
  const qc = useQueryClient();
  const { session } = useSession();
  const isAdmin = session?.user.role === 'ADMIN';

  const rewards = useQuery<ClaimsData>({
    queryKey: ['claims', customerId],
    queryFn: () => api(`/api/claims/${customerId}`),
    enabled: customerId !== null,
    // Le client peut continuer ses achats puis revenir : on rafraîchit le budget
    // au retour sur la fenêtre (et le bouton « Actualiser » le force à la demande).
    refetchOnWindowFocus: true,
    staleTime: 0,
  });

  // Confirmation des choix du client (nom + courriel) — ENREGISTRÉE, pas envoyée.
  // Les courriels partent en lot à la fin du show (bouton admin dans Paramètres).
  const [nom, setNom] = useState('');
  const [courriel, setCourriel] = useState('');
  const [confirme, setConfirme] = useState(false);
  const [confirmMsg, setConfirmMsg] = useState('');
  const emailValide = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(courriel.trim());

  const confirmer = useMutation({
    mutationFn: () =>
      api(`/api/claims/${customerId}/confirm`, {
        method: 'POST',
        body: JSON.stringify({ signerName: nom.trim(), email: courriel.trim() }),
      }),
    onSuccess: () => {
      setConfirmMsg('Choix validés. Le courriel sera envoyé à la fin du show.');
      void qc.invalidateQueries({ queryKey: ['claims', customerId] });
    },
  });

  // Préremplissage une fois par client : on recharge la confirmation enregistrée
  // (nom + courriel) pour la revoir/modifier ; sans écraser une saisie en cours.
  const prefillRef = useRef<number | null>(null);
  useEffect(() => {
    if (customerId === null || !rewards.data) return;
    if (prefillRef.current === customerId) return;
    prefillRef.current = customerId;
    const conf = rewards.data.confirmation;
    setNom(conf?.signerName ?? '');
    setCourriel(conf?.email ?? '');
    setConfirme(false);
    setConfirmMsg('');
  }, [customerId, rewards.data]);

  const setClaim = useMutation({
    mutationFn: ({ tierId, action }: { tierId: number; action: 'add' | 'remove' }) =>
      action === 'add'
        ? api<{ groups: RewardGroup[] }>('/api/claims', {
            method: 'POST',
            body: JSON.stringify({ customerId, tierId }),
          })
        : api<{ groups: RewardGroup[] }>(`/api/claims/${customerId}/${tierId}`, { method: 'DELETE' }),
    onSuccess: (data) => {
      qc.setQueryData<ClaimsData>(['claims', customerId], (old) =>
        old ? { ...old, groups: data.groups } : old,
      );
      void qc.invalidateQueries({ queryKey: ['claims', 'journal'] });
    },
  });

  // Journal global (admin) — monitoring de la distribution.
  const journal = useQuery<{ claims: JournalRow[] }>({
    queryKey: ['claims', 'journal'],
    queryFn: () => api('/api/claims'),
    enabled: isAdmin && customerId === null,
  });
  const retirer = useMutation({
    mutationFn: (row: JournalRow) =>
      api(`/api/claims/${row.customerId}/${row.tierId}`, { method: 'DELETE' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['claims', 'journal'] }),
  });

  if (!controlled && customerId === null) {
    return (
      <div className="space-y-8">
        <section className="space-y-4">
          <h2 className="text-lg font-semibold">Comptoir cadeaux</h2>
          <p className="text-muted-foreground text-sm">
            Cherchez un client pour voir son budget et lui remettre ses cadeaux.
          </p>
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Nom, n° de client ou ville…"
            autoCapitalize="none"
            autoCorrect="off"
            spellCheck={false}
            autoFocus
            className="border-input bg-background h-12 w-full max-w-lg rounded-lg border px-3 text-base"
          />

          {recherche && (
            <div className="grid max-w-lg gap-2">
              {clients.data?.customers.map((c) => (
                <button
                  key={c.id}
                  onClick={() => setOwnCustomerId(c.id)}
                  className="bg-card hover:border-primary/50 flex items-center gap-3 rounded-lg border p-3 text-left"
                >
                  <span className="bg-secondary text-secondary-foreground rounded px-2 py-0.5 font-mono text-xs font-medium">
                    {c.custNum}
                  </span>
                  <span className="font-medium">{c.name}</span>
                </button>
              ))}
              {!clients.isPending && clients.data?.customers.length === 0 && (
                <p className="text-muted-foreground text-sm">Aucun client trouvé.</p>
              )}
            </div>
          )}
        </section>

        {isAdmin && (
          <section className="space-y-3">
            <div className="flex items-baseline justify-between">
              <h2 className="text-lg font-semibold">Distribution des cadeaux</h2>
              <span className="text-muted-foreground text-sm">
                {journal.data?.claims.length ?? 0} cadeau(x) remis
              </span>
            </div>

            {journal.isPending ? (
              <p className="text-muted-foreground text-sm">Chargement…</p>
            ) : (journal.data?.claims.length ?? 0) === 0 ? (
              <p className="text-muted-foreground text-sm">Aucun cadeau remis pour l'instant.</p>
            ) : (
              <div className="bg-card overflow-x-auto rounded-xl border">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-muted-foreground border-b text-left">
                      <th className="p-3 font-medium">Quand</th>
                      <th className="p-3 font-medium">Client</th>
                      <th className="p-3 font-medium">Groupe</th>
                      <th className="p-3 font-medium">Cadeau</th>
                      <th className="p-3 font-medium">Remis par</th>
                      <th className="p-3"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {journal.data!.claims.map((r) => {
                      const enCours = retirer.isPending && retirer.variables?.id === r.id;
                      return (
                        <tr key={r.id} className="border-t">
                          <td className="text-muted-foreground p-3 whitespace-nowrap tabular-nums">
                            {r.claimedAt
                              ? new Date(r.claimedAt).toLocaleString('fr-CA', {
                                  dateStyle: 'short',
                                  timeStyle: 'short',
                                })
                              : '—'}
                          </td>
                          <td className="p-3">
                            <span className="font-medium">{r.customerName}</span>{' '}
                            <span className="text-muted-foreground font-mono text-xs">{r.custNum}</span>
                          </td>
                          <td className="p-3">{r.groupName}</td>
                          <td className="p-3">
                            <span className="font-medium">{r.giftName}</span>{' '}
                            <span className="text-muted-foreground text-xs tabular-nums">
                              ({formatMoney(r.value)})
                            </span>
                          </td>
                          <td className="text-muted-foreground p-3">{r.claimedBy ?? '—'}</td>
                          <td className="p-3 text-right">
                            <button
                              onClick={() => {
                                if (
                                  confirm(
                                    `Annuler la remise de « ${r.giftName} » à ${r.customerName} ?`,
                                  )
                                ) {
                                  retirer.mutate(r);
                                }
                              }}
                              disabled={enCours}
                              className="border-destructive/40 text-destructive rounded-lg border px-3 py-1.5 text-sm font-medium disabled:opacity-40"
                            >
                              {enCours ? '…' : 'Retirer'}
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
            {retirer.isError && (
              <p className="text-destructive text-sm font-medium">{(retirer.error as Error).message}</p>
            )}
          </section>
        )}
      </div>
    );
  }

  const data = rewards.data;
  const pendingTier = setClaim.isPending ? setClaim.variables?.tierId : null;
  // Ne montrer QUE les cadeaux disponibles (à réclamer) ou déjà remis ; masquer un
  // fournisseur sans cadeau dans ces états.
  const groupesVisibles = (data?.groups ?? [])
    .map((g) => ({ ...g, gifts: g.gifts.filter((t) => t.claimable || t.claimedCount > 0) }))
    .filter((g) => g.gifts.length > 0);
  const aDesRemis = (data?.groups ?? []).some((g) => g.gifts.some((t) => t.claimedCount > 0));
  // Il reste des cadeaux À CHOISIR tant qu'un cadeau est encore DISPONIBLE
  // (réclamable) : le budget restant du client permet encore d'en prendre un.
  const resteAChoisir = groupesVisibles.some((g) => g.gifts.some((t) => t.claimable));

  return (
    <div className="space-y-5">
      {/* En mode contrôlé, l'entête client (nom + changer de client) est géré par le parent. */}
      {!controlled && (
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h2 className="text-lg font-semibold">{data?.customer.name ?? 'Client'}</h2>
            {data && (
              <p className="text-muted-foreground font-mono text-sm">{data.customer.custNum}</p>
            )}
          </div>
          <button
            onClick={() => {
              setOwnCustomerId(null);
              setQ('');
            }}
            className="border-input rounded-lg border px-4 py-2 text-sm font-medium"
          >
            ← Autre client
          </button>
        </div>
      )}

      {data?.giftsClosed && (
        <div className="rounded-lg border border-amber-500/50 bg-amber-500/10 p-3 text-sm">
          <p className="font-medium text-amber-700 dark:text-amber-400">Cadeaux clôturés</p>
          <p className="text-muted-foreground mt-0.5">
            Le show est terminé : plus aucune nouvelle remise n'est possible. Les cadeaux déjà remis
            restent affichés.
          </p>
        </div>
      )}

      {rewards.isPending ? (
        <p className="text-muted-foreground text-sm">Chargement…</p>
      ) : groupesVisibles.length === 0 ? (
        <div className="rounded-xl border border-dashed p-10 text-center">
          <p className="font-medium">Aucun cadeau disponible</p>
          <p className="text-muted-foreground mt-1 text-sm">
            Ce client n'a atteint le seuil d'aucun cadeau pour l'instant.
          </p>
        </div>
      ) : (
        groupesVisibles.map((g) => (
          <details key={g.groupId} open className="bg-card group rounded-xl border">
            {/* En-tête repliable : fournisseur + budget restant + chevron. */}
            <summary className="flex cursor-pointer list-none flex-wrap items-center justify-between gap-2 p-5 [&::-webkit-details-marker]:hidden">
              <h3 className="font-semibold">{g.groupName}</h3>
              <span className="flex items-center gap-2">
                <span className="text-muted-foreground text-sm">
                  restant{' '}
                  <span className="text-foreground font-semibold tabular-nums">
                    {formatMoney(g.remaining)}
                  </span>{' '}
                  <span className="tabular-nums">/ {formatMoney(g.budget)}</span>
                </span>
                <span className="text-muted-foreground transition-transform group-open:rotate-180">
                  ▾
                </span>
              </span>
            </summary>
            <div className="border-t px-5 pt-2 pb-4">
              <ul className="divide-y">
                {g.gifts.map((gift) => (
                <li key={gift.tierId} className="flex items-center gap-3 py-2.5">
                  <span className="text-muted-foreground w-20 shrink-0 tabular-nums">
                    {formatMoney(gift.value)}
                  </span>
                  <span className="flex-1 font-medium">
                    {gift.giftName}
                    {gift.claimedCount > 0 && (
                      <span className="ml-2 rounded bg-green-600/15 px-1.5 py-0.5 text-xs font-medium text-green-700 dark:text-green-400">
                        ×{gift.claimedCount} remis
                      </span>
                    )}
                  </span>
                  <div className="flex shrink-0 items-center gap-2">
                    <button
                      type="button"
                      aria-label="Retirer une remise"
                      onClick={() => setClaim.mutate({ tierId: gift.tierId, action: 'remove' })}
                      disabled={gift.claimedCount === 0 || (setClaim.isPending && pendingTier === gift.tierId)}
                      className="border-input h-9 w-9 rounded-lg border text-lg font-bold disabled:opacity-30"
                    >
                      −
                    </button>
                    <span className="w-6 text-center tabular-nums">{gift.claimedCount}</span>
                    <button
                      type="button"
                      aria-label="Remettre ce cadeau"
                      onClick={() => setClaim.mutate({ tierId: gift.tierId, action: 'add' })}
                      disabled={!gift.claimable || (setClaim.isPending && pendingTier === gift.tierId)}
                      title={!gift.claimable ? 'Budget restant insuffisant' : undefined}
                      className="border-primary/40 text-primary h-9 w-9 rounded-lg border text-lg font-bold disabled:opacity-30"
                    >
                      +
                    </button>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          </details>
        ))
      )}

      {setClaim.isError && (
        <p className="text-destructive text-sm font-medium">{(setClaim.error as Error).message}</p>
      )}

      {/* Confirmation du client + envoi de ses cadeaux choisis par courriel. */}
      {groupesVisibles.length > 0 && (
        <section className="bg-card space-y-3 rounded-xl border p-5">
          <h3 className="font-semibold">Confirmation du client</h3>

          {resteAChoisir && (
            <div className="rounded-lg border border-amber-500/50 bg-amber-500/10 p-3 text-sm">
              <p className="font-medium text-amber-700 dark:text-amber-400">
                Il reste des cadeaux à choisir.
              </p>
              <p className="text-muted-foreground mt-0.5">
                Le budget du client permet encore de réclamer au moins un cadeau. Terminez la
                sélection (bouton +) avant de confirmer ses choix.
              </p>
            </div>
          )}

          <label className="flex items-start gap-2 text-sm">
            <input
              type="checkbox"
              checked={confirme && !resteAChoisir}
              disabled={resteAChoisir}
              onChange={(e) => setConfirme(e.target.checked)}
              className="mt-0.5 h-5 w-5 shrink-0 disabled:opacity-40"
            />
            <span className={resteAChoisir ? 'text-muted-foreground' : undefined}>
              Le client confirme ses choix de cadeaux.
            </span>
          </label>

          <label className="block space-y-1.5">
            <span className="text-sm font-medium">Nom et prénom du client</span>
            <input
              value={nom}
              onChange={(e) => {
                setNom(e.target.value);
                setConfirmMsg('');
              }}
              placeholder="Prénom Nom"
              autoCapitalize="words"
              className="border-input bg-background h-11 w-full max-w-sm rounded-lg border px-3"
            />
          </label>

          <label className="block space-y-1.5">
            <span className="text-sm font-medium">Courriel du client</span>
            <input
              type="email"
              value={courriel}
              onChange={(e) => {
                setCourriel(e.target.value);
                setConfirmMsg('');
              }}
              placeholder="client@exemple.com"
              autoCapitalize="none"
              autoCorrect="off"
              spellCheck={false}
              pattern="[^@\s]+@[^@\s]+\.[^@\s]+"
              className={`bg-background h-11 w-full max-w-sm rounded-lg border px-3 ${
                courriel && !emailValide ? 'border-destructive' : 'border-input'
              }`}
            />
            {courriel && !emailValide && (
              <span className="text-destructive block text-xs">Adresse courriel invalide.</span>
            )}
          </label>

          <button
            onClick={() => {
              setConfirmMsg('');
              confirmer.mutate();
            }}
            disabled={
              !confirme ||
              resteAChoisir ||
              nom.trim() === '' ||
              !emailValide ||
              !aDesRemis ||
              confirmer.isPending
            }
            className="bg-primary text-primary-foreground h-11 rounded-lg px-5 font-medium disabled:opacity-40"
          >
            {confirmer.isPending ? 'Enregistrement…' : 'Valider les choix du client'}
          </button>
          <p className="text-muted-foreground text-xs">
            {!aDesRemis
              ? 'Sélectionnez au moins un cadeau (bouton +) avant de valider.'
              : 'Les choix sont enregistrés maintenant ; le courriel de confirmation est envoyé à la fin du show (par l’admin).'}
          </p>
          {confirmMsg && (
            <p className="text-sm font-medium text-green-700 dark:text-green-400">{confirmMsg}</p>
          )}
          {confirmer.isError && (
            <p className="text-destructive text-sm">{(confirmer.error as Error).message}</p>
          )}
        </section>
      )}
    </div>
  );
}
